import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { TableColumn, TableEndpoint } from '@ipfabric/utils';
import { getDeviceViewEntityNodeId } from 'components/DeviceView/helpers/getDeviceViewEntityNodeId';
import type { DeviceViewModel } from 'components/DeviceView/model/DeviceViewModel';
import { selectEntityTab } from 'components/DeviceView/model/commands/selectEntityTab';
import { showLabelsDetail } from 'components/DeviceView/model/commands/showLabelsDetail';
import { showNodeDetail } from 'components/DeviceView/model/commands/showNodeDetail';
import { useDeviceView } from 'components/DeviceView/useDeviceView';
import { getLabelsExplorerSources } from 'components/Table/helpers/getLabelsExplorerSources';
import type {
  DeviceExplorerTabSource,
  LabelsColumnBinding,
  LabelsExplorerTabSource,
  OpenExplorerRequest,
} from 'components/Table/model/ExplorerTableCellContext';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import { useMemo } from 'react';

export type TableDeviceViewHook = {
  deviceView: DeviceViewModel;
  tableCellContext: Partial<TableCellContext>;
  tableClassName?: string;
};

export const getLabelsColumns = <TRow,>(
  columns: TableColumn<TRow>[],
): LabelsColumnBinding[] =>
  columns.flatMap((column) =>
    column.format.type === 'labels'
      ? [
          {
            columnKey: String(column.key),
            targetIdField: String(column.format.targetIdField),
            targetType: column.format.targetType,
            title: column.title,
          },
        ]
      : [],
  );

export const useTableDeviceView = <TRow,>(
  endpoint: Pick<TableEndpoint<TRow>, 'columns' | 'deviceViewAnchor'>,
): TableDeviceViewHook => {
  const deviceView = useDeviceView();
  const labelsColumns = useMemo(
    () => getLabelsColumns(endpoint.columns),
    [endpoint.columns],
  );

  const handleOpenExplorer = useStableCallback(
    (row: unknown, { selectedTabId, sources }: OpenExplorerRequest) => {
      const deviceSource = sources.find(
        (source): source is DeviceExplorerTabSource => source.kind === 'device',
      );
      const labelsSources = [
        ...new Map(
          [
            ...sources.filter(
              (source): source is LabelsExplorerTabSource =>
                source.kind === 'labels',
            ),
            ...getLabelsExplorerSources(row, labelsColumns),
          ].map((source) => [source.columnKey, source]),
        ).values(),
      ];
      const labelsTabs = labelsSources.map((source) => ({
        columnKey: source.columnKey,
        id: source.targetId,
        title: source.title,
        type: source.targetType,
      }));

      if (deviceSource === undefined) {
        const selectedLabelsSource =
          labelsSources.find((source) => source.columnKey === selectedTabId) ??
          labelsSources[0];
        if (selectedLabelsSource !== undefined) {
          deviceView.dispatch(showLabelsDetail, {
            labelsTabs,
            selectedTabId: selectedLabelsSource.columnKey,
            title: selectedLabelsSource.title || 'Labels',
          });
        }
        return;
      }

      deviceView.dispatch(showNodeDetail, {
        extra: null,
        ...(labelsTabs.length > 0 ? { labelsTabs } : {}),
        nodeType: null,
        sn: deviceSource.sn,
        title: deviceSource.hostname,
      });

      if (
        selectedTabId !== undefined &&
        labelsTabs.some((tab) => tab.columnKey === selectedTabId)
      ) {
        deviceView.dispatch(selectEntityTab, {
          deviceViewEntityId: getDeviceViewEntityNodeId(deviceSource.sn),
          tabId: selectedTabId,
        });
      }
    },
  );

  const tableCellContext: Partial<TableCellContext> = useMemo(
    () => ({
      deviceViewAnchor: endpoint.deviceViewAnchor,
      labelsColumns,
      onOpenExplorer: handleOpenExplorer,
    }),
    [endpoint.deviceViewAnchor, labelsColumns],
  );

  // compensate device view negative margin to prevent tabs covering table footer
  const tableClassName = deviceView.deviceViewOpen ? 'mb-8' : undefined;

  return {
    deviceView,
    tableCellContext,
    tableClassName,
  };
};
